import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HomeIcon, UsersIcon, BookOpenIcon, CheckSquareIcon, UserIcon, BellIcon, ChevronRightIcon, AlertCircleIcon, CheckCircle2Icon, ClockIcon, XIcon, ShieldAlertIcon, SmartphoneIcon, ShieldCheckIcon } from 'lucide-react';
// --- Mock Data ---
const FAMILY_MEMBERS = [{
  id: '1',
  name: 'Jason Phillips',
  initials: 'JP',
  role: 'Super Guardian',
  score: 612,
  status: 'On Track',
  statusColor: 'text-[#0EB4CC] bg-[#0EB4CC]/10',
  avatarColor: 'bg-[#1a0647]'
}, {
  id: '2',
  name: 'Lois Brady',
  initials: 'LB',
  role: 'Adult Member',
  score: 412,
  status: 'Needs Attention',
  statusColor: 'text-amber-600 bg-amber-50',
  avatarColor: 'bg-[#8528e2]'
}, {
  id: '3',
  name: 'Dan Phillips',
  initials: 'DP',
  role: 'Adult Member',
  score: 380,
  status: 'Needs Attention',
  statusColor: 'text-amber-600 bg-amber-50',
  avatarColor: 'bg-[#0EB4CC]'
}, {
  id: '4',
  name: 'Leslie Phillips',
  initials: 'LP',
  role: 'Guardian',
  score: 650,
  status: 'On Track',
  statusColor: 'text-[#0EB4CC] bg-[#0EB4CC]/10',
  avatarColor: 'bg-[#631bb7]'
}];
const ACTIVITY_FEED = [{
  id: 1,
  icon: <CheckCircle2Icon className="text-green-500" size={20} data-id="element-1709" />,
  title: 'Mom completed Password Manager setup',
  time: '2 hours ago',
  member: 'Lois Brady'
}, {
  id: 2,
  icon: <AlertCircleIcon className="text-amber-500" size={20} data-id="element-1710" />,
  title: "Dad's phone is running outdated software",
  time: '5 hours ago',
  member: 'Dan Phillips'
}, {
  id: 3,
  icon: <UsersIcon className="text-[#8528e2]" size={20} data-id="element-1711" />,
  title: 'Leslie joined the family plan',
  time: '1 day ago',
  member: 'Leslie Phillips'
}];
export function GuardianDashboardPage() {
  const [activeTab, setActiveTab] = useState('Home');
  const [selectedMember, setSelectedMember] = useState<any | null>(null);
  const [drawerTab, setDrawerTab] = useState('Overview');
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col md:flex-row font-sans" data-id="element-1712">
      {/* --- Desktop Sidebar --- */}
      <aside className="hidden md:flex flex-col w-64 bg-[#1a0647] text-white fixed h-full z-20" data-id="element-1713">
        <div className="p-6" data-id="element-1714">
          <img src="https://cdn.magicpatterns.com/uploads/ddFjEWvaDfXw4mBqdFC5Gd/Caveo_Logotype_Horizontal_240x50_White_PNG.png" alt="Caveo" className="h-6" data-id="element-1715" />
        </div>
        <nav className="flex-1 px-4 space-y-2 mt-4" data-id="element-1716">
          {[{
          name: 'Home',
          icon: HomeIcon
        }, {
          name: 'Family',
          icon: UsersIcon
        }, {
          name: 'Actions',
          icon: BookOpenIcon
        }, {
          name: 'Tasks',
          icon: CheckSquareIcon
        }, {
          name: 'Profile',
          icon: UserIcon
        }].map(item => <button key={item.name} onClick={() => setActiveTab(item.name)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${activeTab === item.name ? 'bg-[#8528e2] text-white font-semibold' : 'text-gray-300 hover:bg-white/10'}`} data-id="element-1717">
              <item.icon size={20} data-id="element-1718" />
              {item.name}
            </button>)}
        </nav>
      </aside>

      {/* --- Main Content --- */}
      <main className="flex-1 md:ml-64 pb-24 md:pb-8" data-id="element-1719">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-1720">
          <h1 className="text-xl md:text-2xl font-bold text-[#1a0647]" data-id="element-1721">
            Good morning, Jason
          </h1>
          <div className="flex items-center gap-4" data-id="element-1722">
            <button className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1723">
              <BellIcon size={24} data-id="element-1724" />
              <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" data-id="element-1725"></span>
            </button>
            <div className="w-10 h-10 rounded-full bg-[#1a0647] text-white flex items-center justify-center font-bold" data-id="element-1726">
              JP
            </div>
          </div>
        </header>

        <div className="p-6 max-w-5xl mx-auto space-y-8" data-id="element-1727">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1728">
            {/* Score Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-5" data-id="element-1729">
              <div className="relative w-16 h-16 shrink-0" data-id="element-1730">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-1731">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#f3f4f6" strokeWidth="10" data-id="element-1732" />
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#0EB4CC" strokeWidth="10" strokeLinecap="round" strokeDasharray="282.7" strokeDashoffset={282.7 - 282.7 * 0.612} data-id="element-1733" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1734">
                  <span className="text-sm font-bold text-[#111111]" data-id="element-1735">612</span>
                </div>
              </div>
              <div data-id="element-1736">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1737">
                  Family Score
                </p>
                <p className="text-sm text-gray-600" data-id="element-1738">
                  <span className="text-[#0EB4CC] font-bold" data-id="element-1739">On Track</span> to
                  reach 1000
                </p>
              </div>
            </div>

            {/* Modules Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-center" data-id="element-1740">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-1741">
                Actions Completed
              </p>
              <div className="flex items-end gap-2 mb-2" data-id="element-1742">
                <span className="text-2xl font-bold text-[#111111] leading-none" data-id="element-1743">
                  14
                </span>
                <span className="text-sm text-gray-500 font-medium mb-0.5" data-id="element-1744">
                  / 37
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1745">
                <div className="h-full bg-[#8528e2] w-[37%] rounded-full" data-id="element-1746"></div>
              </div>
            </div>

            {/* Alerts Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4" data-id="element-1747">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-1748">
                <ShieldAlertIcon size={24} className="text-red-500" data-id="element-1749" />
              </div>
              <div data-id="element-1750">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1751">
                  Active Alerts
                </p>
                <p className="text-2xl font-bold text-[#111111] leading-none" data-id="element-1752">
                  2
                </p>
              </div>
            </div>
          </div>

          {/* Next Action Card */}
          <Link to="/learn/module-7" className="bg-[#8528e2] rounded-2xl p-6 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-6 no-underline hover:bg-[#7322c4] transition-colors" data-id="element-1753">
            <div data-id="element-1754">
              <p className="text-sm font-bold text-[#d0bdf5] uppercase tracking-wider mb-2" data-id="element-1755">
                Your Next Action
              </p>
              <h3 className="text-xl font-bold mb-2" data-id="element-1756">
                Recognizing Scams &amp; Phishing
              </h3>
              <div className="flex items-center gap-2 text-sm text-[#e8dcfc]" data-id="element-1757">
                <ClockIcon size={16} data-id="element-1758" />
                <span data-id="element-1759">Est. time: 5-7 mins</span>
              </div>
            </div>
            <span className="bg-white text-[#8528e2] px-6 py-3 rounded-xl font-bold shrink-0 text-center" data-id="element-1760">
              Start Action
            </span>
          </Link>

          {/* Family Members */}
          <div data-id="element-1761">
            <div className="flex items-center justify-between mb-4" data-id="element-1762">
              <h2 className="text-lg font-bold text-[#111111]" data-id="element-1763">
                Family Members
              </h2>
              <button className="text-sm font-bold text-[#8528e2] hover:text-[#7322c4]" data-id="element-1764">
                Invite Member
              </button>
            </div>
            <div className="flex overflow-x-auto pb-4 -mx-6 px-6 md:mx-0 md:px-0 gap-4 hide-scrollbar" data-id="element-1765">
              {FAMILY_MEMBERS.map(member => <button key={member.id} onClick={() => setSelectedMember(member)} className="bg-white border border-gray-200 rounded-2xl p-5 min-w-[240px] flex-shrink-0 text-left hover:border-[#8528e2] transition-colors shadow-sm" data-id="element-1766">
                  <div className="flex items-start justify-between mb-4" data-id="element-1767">
                    <div className="flex items-center gap-3" data-id="element-1768">
                      <div className={`w-10 h-10 rounded-full ${member.avatarColor} text-white flex items-center justify-center font-bold text-sm`} data-id="element-1769">
                        {member.initials}
                      </div>
                      <div data-id="element-1770">
                        <h3 className="font-bold text-[#111111] leading-tight" data-id="element-1771">
                          {member.name}
                        </h3>
                        <span className="text-xs text-gray-500" data-id="element-1772">
                          {member.role}
                        </span>
                      </div>
                    </div>
                    <div className="relative w-8 h-8 shrink-0" data-id="element-1773">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-1774">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="#f3f4f6" strokeWidth="12" data-id="element-1775" />
                        <circle cx="50" cy="50" r="45" fill="none" stroke={member.score > 0 ? '#0EB4CC' : '#e5e7eb'} strokeWidth="12" strokeLinecap="round" strokeDasharray="282.7" strokeDashoffset={282.7 - 282.7 * (member.score / 1000)} data-id="element-1776" />
                      </svg>
                    </div>
                  </div>
                  <div className={`inline-flex px-2.5 py-1 rounded-md text-xs font-bold ${member.statusColor}`} data-id="element-1777">
                    {member.status}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Activity Feed */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-id="element-1778">
            <h2 className="text-lg font-bold text-[#111111] mb-6" data-id="element-1779">
              Family Activity
            </h2>
            <div className="space-y-6" data-id="element-1780">
              {ACTIVITY_FEED.map((item, index) => <div key={item.id} className="flex gap-4 relative" data-id="element-1781">
                  {index !== ACTIVITY_FEED.length - 1 && <div className="absolute left-5 top-10 bottom-[-24px] w-px bg-gray-200" data-id="element-1782"></div>}
                  <div className="w-10 h-10 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 z-10" data-id="element-1783">
                    {item.icon}
                  </div>
                  <div className="pt-2" data-id="element-1784">
                    <p className="text-sm font-medium text-[#111111]" data-id="element-1785">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1786">{item.time}</p>
                  </div>
                </div>)}
            </div>
            <button className="w-full mt-6 py-3 text-sm font-bold text-[#8528e2] bg-[#f0ebf8] rounded-xl hover:bg-[#e4d5f5] transition-colors" data-id="element-1787">
              View All Activity
            </button>
          </div>
        </div>
      </main>

      {/* --- Mobile Bottom Tab Bar --- */}
      <nav className="md:hidden fixed bottom-0 w-full bg-white border-t border-gray-200 flex items-center justify-around pb-safe pt-2 px-2 z-20" data-id="element-1788">
        {[{
        name: 'Home',
        icon: HomeIcon
      }, {
        name: 'Family',
        icon: UsersIcon
      }, {
        name: 'Actions',
        icon: BookOpenIcon
      }, {
        name: 'Tasks',
        icon: CheckSquareIcon
      }, {
        name: 'Profile',
        icon: UserIcon
      }].map(item => <button key={item.name} onClick={() => setActiveTab(item.name)} className={`flex flex-col items-center p-2 min-w-[64px] ${activeTab === item.name ? 'text-[#8528e2]' : 'text-gray-400'}`} data-id="element-1789">
            <item.icon size={24} className="mb-1" data-id="element-1790" />
            <span className="text-[10px] font-medium" data-id="element-1791">{item.name}</span>
          </button>)}
      </nav>

      {/* --- Member Detail Drawer --- */}
      <AnimatePresence data-id="element-1792">
        {selectedMember && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedMember(null)} className="fixed inset-0 bg-black/40 z-40" data-id="element-1793" />

            {/* Drawer */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-y-0 right-0 w-full md:w-[400px] bg-white shadow-2xl z-50 flex flex-col" data-id="element-1794">
              {/* Drawer Header */}
              <div className="p-6 border-b border-gray-100 bg-[#f8f8f8]" data-id="element-1795">
                <div className="flex items-start justify-between mb-6" data-id="element-1796">
                  <div className="flex items-center gap-4" data-id="element-1797">
                    <div className={`w-14 h-14 rounded-full ${selectedMember.avatarColor} text-white flex items-center justify-center font-bold text-xl`} data-id="element-1798">
                      {selectedMember.initials}
                    </div>
                    <div data-id="element-1799">
                      <h2 className="text-xl font-bold text-[#111111]" data-id="element-1800">
                        {selectedMember.name}
                      </h2>
                      <span className="text-sm text-gray-500" data-id="element-1801">
                        {selectedMember.role}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedMember(null)} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition-colors" data-id="element-1802">
                    <XIcon size={20} data-id="element-1803" />
                  </button>
                </div>

                <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-200" data-id="element-1804">
                  <div className="relative w-12 h-12 shrink-0" data-id="element-1805">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-1806">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#f3f4f6" strokeWidth="10" data-id="element-1807" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke={selectedMember.score > 0 ? '#0EB4CC' : '#e5e7eb'} strokeWidth="10" strokeLinecap="round" strokeDasharray="282.7" strokeDashoffset={282.7 - 282.7 * (selectedMember.score / 1000)} data-id="element-1808" />
                    </svg>
                  </div>
                  <div data-id="element-1809">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-1810">
                      Protection Score
                    </p>
                    <p className="text-lg font-bold text-[#111111] leading-none" data-id="element-1811">
                      {selectedMember.score}{' '}
                      <span className="text-sm text-gray-400 font-normal" data-id="element-1812">
                        / 1000
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Drawer Tabs */}
              <div className="flex border-b border-gray-200 px-6" data-id="element-1813">
                {['Overview', 'Progress', 'Permissions'].map(tab => <button key={tab} onClick={() => setDrawerTab(tab)} className={`pb-3 pt-4 px-4 text-sm font-bold relative ${drawerTab === tab ? 'text-[#8528e2]' : 'text-gray-500 hover:text-gray-800'}`} data-id="element-1814">
                    {tab}
                    {drawerTab === tab && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#8528e2]" data-id="element-1815" />}
                  </button>)}
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-1816">
                {drawerTab === 'Overview' && <div className="space-y-6" data-id="element-1817">
                    <div data-id="element-1818">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-1819">
                        Top Tasks
                      </h3>
                      <div className="space-y-3" data-id="element-1820">
                        {['Update iPhone to iOS 17.4', 'Enable MFA on primary email', 'Complete Scam Recognition module'].map((task, i) => <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50" data-id="element-1821">
                            <div className="w-5 h-5 rounded border border-gray-300 mt-0.5 bg-white shrink-0" data-id="element-1822"></div>
                            <span className="text-sm text-[#111111]" data-id="element-1823">
                              {task}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    <div className="space-y-3 pt-4" data-id="element-1824">
                      <button className="w-full py-3.5 bg-[#8528e2] text-white rounded-xl font-bold hover:bg-[#7322c4] transition-colors shadow-sm" data-id="element-1825">
                        Send a Nudge
                      </button>
                      <button className="w-full py-3.5 bg-white border border-gray-200 text-[#111111] rounded-xl font-bold hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1826">
                        Assist This Member
                      </button>
                    </div>
                  </div>}

                {drawerTab === 'Progress' && <div className="space-y-6" data-id="element-1827">
                    {[{
                level: 'Essential Protection',
                progress: 80
              }, {
                level: 'Advanced Protection',
                progress: 30
              }, {
                level: 'Full Coverage',
                progress: 0
              }].map((lvl, i) => <div key={i} data-id="element-1828">
                        <div className="flex justify-between text-sm mb-2" data-id="element-1829">
                          <span className="font-bold text-[#111111]" data-id="element-1830">
                            {lvl.level}
                          </span>
                          <span className="text-gray-500" data-id="element-1831">{lvl.progress}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1832">
                          <div className="h-full bg-[#0EB4CC] rounded-full" style={{
                    width: `${lvl.progress}%`
                  }} data-id="element-1833"></div>
                        </div>
                      </div>)}
                  </div>}

                {drawerTab === 'Permissions' && <div className="space-y-6" data-id="element-1834">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3" data-id="element-1835">
                      <ShieldCheckIcon className="text-blue-500 shrink-0" size={20} data-id="element-1836" />
                      <p className="text-xs text-blue-800 leading-relaxed" data-id="element-1837">
                        These settings are controlled by the member. You can
                        request changes, but they must approve them.
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-1838">
                      {[{
                  name: 'View Progress Score',
                  enabled: true
                }, {
                  name: 'Receive Security Alerts',
                  enabled: true
                }, {
                  name: 'Remote Assist Access',
                  enabled: false
                }].map((perm, i) => <div key={i} className="flex items-center justify-between py-2" data-id="element-1839">
                          <span className="text-sm font-medium text-[#111111]" data-id="element-1840">
                            {perm.name}
                          </span>
                          <div className={`w-11 h-6 rounded-full flex items-center px-1 transition-colors ${perm.enabled ? 'bg-[#0EB4CC]' : 'bg-gray-200'}`} data-id="element-1841">
                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${perm.enabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-1842"></div>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}